@layer refine.base {
  :host {
    display: inline-block;
    min-width: var(--select-min-width, 12rem);
  }

  :host([fullwidth]) {
    display: block;
    width: 100%;
  }

  /* Hide native <option> elements — we render our own UI */
  ::slotted(option),
  ::slotted(optgroup) {
    display: none;
  }

  /* ore-input is readonly so pointer defaults to text — override to pointer.
     `width: 100%` matters just as much: `:host` is `display: inline-block`, which — unlike a
     flex/grid container with `align-items`/`justify-items: stretch` — never stretches its own
     children to fill its own box. Without this, `.trigger` (an `ore-input`, whose own `:host`
     only guarantees a 12rem *minimum*) stays shrink-wrapped to that minimum even when this
     select's own host has already been stretched wider by an ancestor grid/flex track — visibly
     narrower than a sibling field (e.g. `ore-number-input`) occupying the same-width track. */
  .trigger {
    width: 100%;
    cursor: pointer;
  }

  .trigger::part(input) {
    cursor: pointer;
    caret-color: transparent;
    user-select: none;
  }

  /* Chips slot inside ore-input prefix */
  .chips-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--size-1);
    align-items: center;
    min-width: 0;
  }

  .chips-row[hidden] {
    display: none;
  }

  /* Suffix: loader (left) + chevron (right) */
  .trigger-suffix {
    display: flex;
    gap: var(--size-1);
    align-items: center;
    color: var(--color-contrast-500);
  }

  .trigger-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform var(--transition-fast);
  }

  :host([open]) .trigger-icon {
    transform: rotate(180deg);
  }

  .loader {
    display: none;
    flex-shrink: 0;
    width: 1em;
    height: 1em;
    border: 2px solid currentcolor;
    border-inline-end-color: transparent;
    border-radius: 50%;
    animation: var(--_motion-animation, spin var(--transition-loader) infinite);
  }

  /* Keyframe must live in shadow DOM — @keyframes don't inherit across shadow roots */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  :host([loading]) .loader {
    display: inline-block;
  }

  /* ========================================
       Dropdown Listbox
       ======================================== */

  .dropdown {
    position: fixed;
    inset: 0 auto auto 0;
    z-index: var(--z-dropdown, 8000);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--size-0-5);
    max-height: 260px;
    padding: var(--size-1);
    margin: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    text-align: start;
    pointer-events: none;
    background: var(--color-canvas);
    border: var(--border) solid var(--color-contrast-200);
    border-radius: var(--_radius);
    box-shadow: var(--shadow-lg);

    /* Hidden by default */
    opacity: 0;
    backdrop-filter: blur(var(--blur-md));
    transform: translateY(var(--overlay-enter-translate-y));
    transition: var(
      --_motion-transition,
      opacity var(--transition-fast),
      transform var(--transition-fast),
      display var(--transition-fast) allow-discrete
    );
  }

  .dropdown[data-open] {
    pointer-events: auto;
    opacity: 1;
    transform: translateY(0);

    @starting-style {
      opacity: 0;
      transform: translateY(var(--overlay-enter-translate-y));
    }
  }

  .options-list {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  /* ========================================
       Option Items
       ======================================== */

  .option {
    display: flex;
    gap: var(--size-2);
    align-items: center;
    padding: var(--size-1-5) var(--size-2-5);
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
    cursor: pointer;
    border-radius: var(--rounded-sm);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  /* Truncate long labels to keep option rows compact and readable. */
  .option > span:first-child {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .option:hover:not([data-disabled]) {
    background: var(--color-contrast-100);
  }

  .option[data-focused]:not([data-disabled]) {
    color: var(--_theme-base);
    background: color-mix(in oklch, var(--_theme-base) 12%, var(--color-contrast-100));
  }

  .option[data-selected] {
    font-weight: var(--font-medium);
    color: var(--_theme-base);
    background: color-mix(in oklch, var(--_theme-base) 10%, var(--color-contrast-50));
  }

  .option[data-selected][data-focused] {
    background: color-mix(in oklch, var(--_theme-base) 20%, var(--color-contrast-100));
  }

  .option[data-disabled] {
    color: var(--color-contrast-400);
    cursor: not-allowed;
    opacity: 0.6;
  }

  .option-check {
    display: inline-flex;
    flex-shrink: 0;
    margin-inline-start: auto;
    color: var(--_theme-base);
    opacity: 0;
    transition: opacity var(--transition-fast);
  }

  .option[data-selected] .option-check {
    opacity: 1;
  }

  /* ========================================
       Option Groups
       ======================================== */

  .optgroup-label {
    padding: var(--size-1-5) var(--size-2-5) var(--size-1);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-500);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wide, 0.05em);
  }

  /* ========================================
       Loading / No-Results
       ======================================== */

  .dropdown-loading,
  .dropdown-empty {
    padding: var(--size-3) var(--size-2-5);
    font-size: var(--text-sm);
    color: var(--color-contrast-500);
    text-align: center;
  }
}
